import { HeaderPlan } from './HeaderPlan'
import { History, Loader2, SlidersHorizontal } from 'lucide-react'
import type { LangCode } from '@/lib/baba-api'
import { LanguageSwitcherIcon } from '@/components/icons/baba-icons'
import type { HistoryRow, HistoryRowMedia } from '@/lib/library/historyPanelRows'
import type { RecentTranslation } from './types'
import { TranslatorLangPicker } from './TranslatorLangPicker'
import { HistoryPanel } from './HistoryPanel'
import { WsButtonPrimary, WsButtonSecondary, WsIconButton } from './workspace'

/**
 * Phone page header for Translate: the page name and the one action the
 * desktop toolbar keeps behind the command palette button. 52px, like every
 * other phone header (see PageHeader).
 */
export function MobilePageHeader({ title, onOpenCommands }: { title: string; onOpenCommands: () => void }) {
  return (
    <div
      className="md:hidden flex items-center justify-between gap-2 shrink-0"
      style={{ height: 52, padding: '0 8px 0 16px', background: 'var(--cream-2)', borderBottom: '1px solid var(--line)' }}
    >
      <h1 className="truncate" style={{ fontFamily: 'var(--font-ui)', fontSize: 16, fontWeight: 600, letterSpacing: '-0.01em', color: 'var(--ink)', lineHeight: 1.2 }}>
        {title}
      </h1>
      <div className="flex items-center gap-1 shrink-0 min-w-0">
        <HeaderPlan />
        <WsIconButton icon={<SlidersHorizontal size={20} />} label="Translation options" size={44} onClick={onOpenCommands} className="shrink-0 cursor-pointer" />
      </div>
    </div>
  )
}

export function MobileHeader({
  sourceLang,
  targetLang,
  onSourceLangChange,
  onTargetLangChange,
  onSwap,
  onOpenHistory,
}: {
  sourceLang: LangCode
  targetLang: LangCode
  onSourceLangChange: (v: LangCode) => void
  onTargetLangChange: (v: LangCode) => void
  onSwap: () => void
  onOpenHistory: () => void
}) {
  return (
    <div className="md:hidden shrink-0 min-w-0" style={{ background: 'var(--cream-2)' }}>
      {/*
       * One joined 44px control: [source | swap | target], plus the history
       * button. Every item shrinks (`min-w-0` + `flex-1`), so a longer language
       * name or a fallback font can only make a segment narrower, never push
       * the trailing button off the phone.
       */}
      <div className="flex items-center gap-2 px-4 py-2.5 border-b border-app min-w-0 overflow-hidden">
        <div className="flex items-center flex-1 min-w-0 overflow-hidden" style={{ height: 44, borderRadius: 6, border: '1px solid var(--line)', background: 'var(--card)' }}>
          <TranslatorLangPicker
            value={sourceLang}
            onChange={onSourceLangChange}
            side="source"
            disabledCode={targetLang}
            className="flex-1 min-w-0 justify-center h-full! border-0! rounded-none! bg-transparent! text-[15px] cursor-pointer"
          />
          <button
            type="button"
            onClick={onSwap}
            aria-label="Swap languages"
            className="flex items-center justify-center shrink-0 h-full transition-colors duration-150 active:bg-[var(--card-2)] cursor-pointer"
            style={{ width: 44, borderLeft: '1px solid var(--line-hair)', borderRight: '1px solid var(--line-hair)', color: 'var(--accent-deep)' }}
          >
            <LanguageSwitcherIcon size={17} />
          </button>
          <TranslatorLangPicker
            value={targetLang}
            onChange={onTargetLangChange}
            side="target"
            disabledCode={sourceLang}
            className="flex-1 min-w-0 justify-center h-full! border-0! rounded-none! bg-transparent! text-[15px] cursor-pointer"
          />
        </div>
        <WsIconButton icon={<History size={20} />} label="Recent translations" size={44} onClick={onOpenHistory} className="shrink-0 cursor-pointer bg-[var(--card)]! border! border-app" />
      </div>
    </div>
  )
}

/**
 * The single primary action on mobile. The source pane's own Translate button
 * and its keyboard hint are desktop-only (see TranslatorSourcePane), so the
 * phone shows one Translate, and the character counter lives once — in the
 * source pane header, next to the text it counts.
 */
export function MobileBottomBar({
  isTranslating,
  canTranslate,
  hasCompletedTranslation = false,
  onTranslate,
  onCancel,
  onNewTranslation,
}: {
  isTranslating: boolean
  canTranslate: boolean
  hasCompletedTranslation?: boolean
  onTranslate: () => void
  onCancel: () => void
  onNewTranslation?: () => void
}) {
  const showNew = hasCompletedTranslation && !isTranslating && !!onNewTranslation
  return (
    <div className="md:hidden shrink-0 flex items-center gap-2 px-4 py-3 min-w-0" style={{ background: 'var(--cream)' }}>
      {showNew && (
        <WsButtonSecondary h={48} onClick={onNewTranslation} className="grow basis-0 min-w-0 justify-center px-4 text-[15px] font-semibold cursor-pointer">
          New
        </WsButtonSecondary>
      )}
      {isTranslating ? (
        <WsButtonPrimary onClick={onCancel} className="grow-[2] basis-0 min-w-0 justify-center h-12 px-4 text-[15px] font-semibold cursor-pointer">
          <Loader2 size={15} className="animate-spin" aria-hidden />
          Stop
        </WsButtonPrimary>
      ) : (
        <WsButtonPrimary
          onClick={onTranslate}
          disabled={!canTranslate}
          className={`grow-[2] basis-0 min-w-0 justify-center h-12 px-4 text-[15px] font-semibold tracking-tight transition-colors duration-150 cursor-pointer ${
            canTranslate ? 'shadow-md shadow-brand-500/25 active:scale-[0.98]' : 'opacity-40 cursor-not-allowed'
          }`}
        >
          {hasCompletedTranslation ? 'Translate Again' : 'Translate'}
        </WsButtonPrimary>
      )}
    </div>
  )
}

export function MobileHistorySheet({
  open,
  onClose,
  recents,
  activeId,
  onSelectText,
  onOpenMedia,
  onClearLocal,
}: {
  open: boolean
  onClose: () => void
  recents: RecentTranslation[]
  activeId?: string | null
  onSelectText: (row: HistoryRow) => void
  onOpenMedia: (media: HistoryRowMedia) => void
  onClearLocal: () => void
}) {
  if (!open) return null
  return (
    <div className="md:hidden fixed inset-0 z-[9000] flex flex-col justify-end">
      <div className="absolute inset-0" style={{ background: 'rgba(0,0,0,0.4)' }} onClick={onClose} />
      <div
        className="sheet-h-60 relative flex flex-col p-2.5 gap-0"
        style={{ background: 'var(--panel, var(--cream-2))', borderRadius: '12px 12px 0 0', boxShadow: 'var(--shadow-lg)', paddingBottom: 'calc(10px + env(safe-area-inset-bottom))' }}
      >
        <div className="mx-auto mb-2 shrink-0" style={{ width: 36, height: 4, borderRadius: 2, background: 'var(--line)' }} />
        <HistoryPanel
          recents={recents}
          activeId={activeId}
          onSelectText={onSelectText}
          onOpenMedia={onOpenMedia}
          onClearLocal={onClearLocal}
          // Closes the sheet after a restore AND after a media row opens its
          // modal. The sheet sits at z-[9000] and the modal portals in at
          // z-[200], so leaving it up would bury the result the tap asked
          // for — and the Translator, not this sheet, owns that modal, so
          // closing here does not unmount it.
          onRowOpened={onClose}
        />
      </div>
    </div>
  )
}
